// apps/client/src/pages/leads/GrowthInboxPage.tsx
//
// VALUTTEN Growth inbox (wave 2 S2-D.1): the tenant's detector signals, read
// through the listGrowthSignals callable (company from the token, safe
// projection applied server-side) and decided through decideGrowthSignal.
//
//   - Open: undecided and detected within the open window; Approve, or
//     Dismiss with a required reason.
//   - Decided: approved or dismissed within the open window.
//   - Expired: undecided and older; read-only, no actions (the server
//     refuses a decision on them).
//
// Wave 4 (S4-C.8): a signal with a proposal shows its kind, exact change,
// hypothesis, compliance, tier and verdict. Approve and Dismiss carry the
// proposalId; a high-tier proposal shows "Awaiting VALUTTEN review" instead
// of Approve (the founder approves it); an approved proposal offers
// "Mark applied". No cost is shown to any role.
//
// Route: /leads/growth behind RequireGrowthPropose (flag + lead_capture
// add-on) and RequireLeadTab (super_admin, administrator, marketing).

import React, { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { Lightbulb, CheckCircle, XCircle, Clock, History, ShieldCheck, Wrench } from 'lucide-react';
import { functions } from '../../lib/firebase';
import { PageHeader } from '../../components/common/PageHeader';
import StatCard from '../../components/common/StatCard';
import Modal from '../../components/common/Modal';
import Spinner from '../../components/common/Spinner';
import { SkeletonStatCards, SkeletonTable } from '../../components/common/SkeletonLoader';

export interface GrowthSignal {
  signal_id: string;
  detector: string;
  cause_class: string;
  severity: string;
  entity_type: string;
  entity_key: string;
  metric: string;
  unit: string;
  observed_value: number | null;
  expected_value: number | null;
  window_start: string | null;
  window_end: string | null;
  detected_at: string;
  /** Wave 4: the cause's risk tier (high = approved only by VALUTTEN); absent on wave 2 rows. */
  risk_tier?: 'low' | 'medium' | 'high' | string;
  /** Wave 4: the proposal drafted for this signal, or null/absent. */
  proposal?: GrowthProposalView | null;
}

/** The server's safe projection of a proposal (no cost, evidence, agent or model). */
export interface GrowthProposalView {
  proposal_id: string;
  kind: string;
  risk_tier: 'low' | 'medium' | 'high' | string;
  title: string;
  hypothesis: string;
  exact_change: Record<string, string | number | string[] | null>;
  compliance: { status: string; rules: string[]; reasons: string[] };
  state: string;
  verdict: string | null;
}

export interface DecidedGrowthSignal extends GrowthSignal {
  state: 'approved' | 'dismissed' | 'applied';
  decided_at: string;
}

type DecideAction = 'approve' | 'dismiss' | 'applied';

export const AWAITING_REVIEW_LABEL = 'Awaiting VALUTTEN review';

const KIND_LABELS: Record<string, string> = {
  title_meta: 'Title and meta description',
  content_refresh: 'Content refresh',
  internal_link: 'Internal link',
  schema: 'Structured data',
  redirect_fix: 'Redirect fix',
  pause_ad: 'Pause campaign',
  add_negatives: 'Negative keywords',
  budget_note: 'Budget note',
  observe: 'Observation',
  content_brief: 'Content brief',
  review_request_batch: 'Review requests',
  creative_variants: 'Ad copy variants',
};

const VERDICT_LABELS: Record<string, string> = {
  win: 'Win',
  loss: 'Loss',
  inconclusive: 'Inconclusive',
  not_judgeable: 'Not judgeable',
  not_applied: 'Not applied within 28 days',
};

const STATE_LABELS: Record<string, string> = { approved: 'Approved', dismissed: 'Dismissed', applied: 'Applied' };

const COMPLIANCE_CLASS: Record<string, string> = {
  pass: 'text-emerald-300',
  flag: 'text-amber-300',
  block: 'text-red-300',
};

export interface GrowthInbox {
  open: GrowthSignal[];
  decided: DecidedGrowthSignal[];
  expired: GrowthSignal[];
  openSignalDays: number;
}

/** One `/ 100` at presentation: cents to AUD, x100 to a two-decimal figure, counts as-is. */
export function formatGrowthValue(value: number | null, unit: string): string {
  if (value === null || value === undefined) return 'n/a';
  if (unit === 'cents') {
    return (value / 100).toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });
  }
  if (unit === 'x100') {
    return (value / 100).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  return value.toLocaleString('en-AU');
}

/** The callable error's code without the `functions/` prefix, its message and details. */
function callableError(err: unknown): { code: string; message: string; details: unknown } {
  const e = err as { code?: unknown; message?: unknown; details?: unknown } | null;
  return {
    code: typeof e?.code === 'string' ? e.code.replace(/^functions\//, '') : '',
    message: typeof e?.message === 'string' ? e.message : String(err),
    details: e?.details,
  };
}

/** Shown when the server answers accepted: false, or refuses a signal already decided. */
export const supersededMessage = (state: string): string =>
  `Another user decided this signal at the same time; it is now ${state}. Your decision was not applied.`;

/** Shown for every gate refusal: flag not at propose, subscription blocked, add-on missing. */
export const GROWTH_UNAVAILABLE_MESSAGE = 'Growth signals are not available for this company right now.';

// What the server actually throws (services/client-functions/src/growth/decisions):
// - listGrowthSignals.ts enforceGrowthInboxGates: failed-precondition
//   'Growth signals are not enabled for this company.' (flag not at propose);
//   requireFeature: permission-denied with details.feature 'lead_capture';
//   enforceActiveSubscription (subscription/usageTracking.ts): permission-denied
//   with one of the three SUBSCRIPTION_MESSAGES.
// - decideGrowthSignal.ts: failed-precondition 'signal_expired';
//   failed-precondition 'This signal is already <state>.' (invalid transition);
//   wave 4: failed-precondition 'awaiting_valutten_review' (high tier) and
//   'proposal_required' (no proposal to approve or apply).
const NOT_PROPOSING_MESSAGE = 'Growth signals are not enabled for this company.';
const SUBSCRIPTION_MESSAGES = new Set([
  'Your account is suspended. Please update your payment method or contact support.',
  'Your account has been cancelled. Please reactivate your subscription to continue.',
  'Subscription is not active. Please contact support.',
]);
// Wave 4 adds the `applied` state.
const ALREADY_DECIDED = /^This signal is already (approved|dismissed|applied)\.$/;

/** Whether a callable refusal came from the flag, subscription or entitlement gate. */
function isGateRefusal(err: unknown): boolean {
  const { code, message, details } = callableError(err);
  if (code === 'failed-precondition') return message === NOT_PROPOSING_MESSAGE;
  if (code === 'permission-denied') {
    const feature = (details as { feature?: unknown } | null | undefined)?.feature;
    return feature === 'lead_capture' || SUBSCRIPTION_MESSAGES.has(message);
  }
  return false;
}

/** A plain message for a decideGrowthSignal refusal (never a raw code). */
export function decideErrorMessage(err: unknown): string {
  const { code, message } = callableError(err);
  if (isGateRefusal(err)) return GROWTH_UNAVAILABLE_MESSAGE;
  if (code === 'failed-precondition') {
    if (message === 'signal_expired') {
      return 'This signal has expired and can no longer be decided. It is now listed under Expired.';
    }
    if (message === 'awaiting_valutten_review') {
      return 'This change is reviewed by VALUTTEN before it can be approved. You can still dismiss it.';
    }
    if (message === 'proposal_required') {
      return 'No proposal is available for this signal yet. You can dismiss it.';
    }
    const decided = ALREADY_DECIDED.exec(message);
    if (decided) return supersededMessage(decided[1]);
    return 'The decision was not saved. Try again.';
  }
  if (code === 'not-found') return 'This signal no longer exists. The list has been refreshed.';
  if (code === 'invalid-argument') {
    return /email|phone/i.test(message)
      ? 'The reason must not contain an email address or phone number.'
      : 'Enter a reason of up to 500 characters.';
  }
  if (code === 'permission-denied') return 'You do not have access to decide Growth signals.';
  return 'The decision was not saved. Try again.';
}

/** A plain message for a listGrowthSignals failure (never a raw error). */
export function listErrorMessage(err: unknown): string {
  if (isGateRefusal(err)) return GROWTH_UNAVAILABLE_MESSAGE;
  if (callableError(err).code === 'permission-denied') return 'You do not have access to Growth signals.';
  return 'Growth signals could not be loaded. Try again.';
}

/** Refusals after which the loaded list is out of date (wave 4: a proposal may have landed since the load). */
const isStaleSignalError = (err: unknown): boolean => {
  const { code, message } = callableError(err);
  return (
    code === 'not-found' || message === 'signal_expired' || message === 'proposal_required' || ALREADY_DECIDED.test(message)
  );
};

const humanise = (s: string): string => s.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Australia/Brisbane' });

const SEVERITY_CLASS: Record<string, string> = {
  high: 'bg-red-500/15 text-red-300 border-red-500/30',
  medium: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  low: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
};

/** One exact-change value as shown; a null campaign is the ad account grain (W4-D27). */
export function exactChangeValue(field: string, v: string | number | string[] | null): string {
  if (v === null) return field === 'campaignId' ? 'ad account' : 'n/a';
  return Array.isArray(v) ? v.join(', ') : String(v);
}

const ProposalDetail: React.FC<{ proposal: GrowthProposalView }> = ({ proposal }) => {
  const { kind: _kind, ...fields } = proposal.exact_change;
  return (
    <div data-testid="growth-proposal" className="mt-2 space-y-1 rounded-md border border-gray-700/60 bg-gray-900/40 p-3 text-xs text-gray-300">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-gray-100">{KIND_LABELS[proposal.kind] ?? humanise(proposal.kind)}</span>
        <span className="uppercase tracking-wider text-gray-500">{proposal.risk_tier} risk</span>
        {proposal.risk_tier === 'high' && (
          <span className="inline-flex items-center gap-1 text-amber-300">
            <ShieldCheck className="h-3.5 w-3.5" />
            {AWAITING_REVIEW_LABEL}
          </span>
        )}
        {proposal.verdict && <span className="text-gray-400">Result: {VERDICT_LABELS[proposal.verdict] ?? humanise(proposal.verdict)}</span>}
      </div>
      <p className="text-gray-200">{proposal.hypothesis}</p>
      <dl className="grid grid-cols-1 gap-x-3 gap-y-0.5 sm:grid-cols-[auto_1fr]">
        {Object.entries(fields).map(([k, v]) => (
          <React.Fragment key={k}>
            <dt className="text-gray-500">{humanise(k)}</dt>
            <dd className="break-words font-mono text-gray-200">{exactChangeValue(k, v)}</dd>
          </React.Fragment>
        ))}
      </dl>
      <div className={COMPLIANCE_CLASS[proposal.compliance.status] ?? 'text-gray-400'}>
        Compliance: {humanise(proposal.compliance.status)}
        {proposal.compliance.reasons.length > 0 && ` (${proposal.compliance.reasons.join('; ')})`}
      </div>
    </div>
  );
};

const SignalRow: React.FC<{ signal: GrowthSignal; children?: React.ReactNode; trailing?: React.ReactNode }> = ({
  signal,
  children,
  trailing,
}) => (
  <li className="flex flex-col gap-3 border-b border-gray-700/60 py-3 last:border-b-0 md:flex-row md:items-center md:justify-between">
    <div className="min-w-0 space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${SEVERITY_CLASS[signal.severity] ?? SEVERITY_CLASS.low}`}
        >
          {humanise(signal.severity)}
        </span>
        <span className="text-sm font-semibold text-gray-100">{humanise(signal.cause_class)}</span>
        <span className="text-xs uppercase tracking-wider text-gray-500">{signal.detector}</span>
      </div>
      <div className="truncate font-mono text-sm text-gray-300" title={signal.entity_key}>
        {signal.entity_key}
      </div>
      <div className="text-xs text-gray-400">
        {humanise(signal.metric)}: {formatGrowthValue(signal.observed_value, signal.unit)} observed,{' '}
        {formatGrowthValue(signal.expected_value, signal.unit)} expected
        {signal.window_start && signal.window_end ? ` (${signal.window_start} to ${signal.window_end})` : ''}
        {' · detected '}
        {formatDate(signal.detected_at)}
      </div>
      {signal.proposal && <ProposalDetail proposal={signal.proposal} />}
      {trailing}
    </div>
    {children && <div className="flex shrink-0 gap-2">{children}</div>}
  </li>
);

const Section: React.FC<{
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  empty: string;
  count: number;
  testId: string;
  children: React.ReactNode;
}> = ({ title, icon: Icon, empty, count, testId, children }) => (
  <section data-testid={testId} className="rounded-lg border border-gray-700 bg-gray-800/40 p-4">
    <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-100">
      <Icon className="h-4 w-4 text-blue-400" />
      {title}
    </h3>
    {count === 0 ? <p className="text-sm text-gray-400">{empty}</p> : <ul>{children}</ul>}
  </section>
);

const GrowthInboxPage: React.FC = () => {
  const [inbox, setInbox] = useState<GrowthInbox | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // A list failure is kept apart from a decision refusal: the reload a stale
  // refusal triggers clears only the former, so the refusal stays readable.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [dismissing, setDismissing] = useState<GrowthSignal | null>(null);
  const [reason, setReason] = useState('');
  const [dismissError, setDismissError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(null);
    httpsCallable<Record<string, never>, GrowthInbox>(functions, 'listGrowthSignals')({})
      .then((r) => {
        if (active) setInbox(r.data);
      })
      .catch((err) => {
        console.error('[GrowthInboxPage] list failed', err);
        if (active) setLoadError(listErrorMessage(err));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [refreshKey]);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  const decide = useCallback(
    async (signal: GrowthSignal, action: DecideAction, why?: string) => {
      const signalId = signal.signal_id;
      setBusyId(signalId);
      setError(null);
      setNotice(null);
      setDismissError(null);
      try {
        const proposalId = signal.proposal?.proposal_id;
        const res = await httpsCallable<
          { signalId: string; action: DecideAction; reason?: string; proposalId?: string },
          { state: string; accepted: boolean }
        >(functions, 'decideGrowthSignal')({
          signalId,
          action,
          ...(why ? { reason: why } : {}),
          ...(proposalId ? { proposalId } : {}),
        });
        if (!res.data.accepted) setNotice(supersededMessage(res.data.state));
        setDismissing(null);
        setReason('');
        refresh();
      } catch (err) {
        console.error('[GrowthInboxPage] decide failed', err);
        const message = decideErrorMessage(err);
        // A failed dismiss keeps its modal open, so the message shows there.
        if (action === 'dismiss') setDismissError(message);
        else setError(message);
        if (isStaleSignalError(err)) refresh();
      } finally {
        setBusyId(null);
      }
    },
    [refresh],
  );

  const days = inbox?.openSignalDays ?? 30;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Lightbulb}
        title="Growth Signals"
        subtitle={`Search and ad spend signals for your business. Open signals expire after ${days} days without a decision.`}
        onRefresh={refresh}
        isRefreshing={loading}
      />

      {(loadError ?? error) && (
        <div role="alert" className="rounded-md border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300">
          {loadError ?? error}
        </div>
      )}

      {notice && (
        <div role="status" className="rounded-md border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-200">
          {notice}
        </div>
      )}

      {loading && !inbox ? (
        <>
          <SkeletonStatCards count={3} columns={3} />
          <SkeletonTable rows={4} columns={4} />
        </>
      ) : inbox ? (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <StatCard title="Open" value={inbox.open.length} subtitle={`Awaiting a decision (last ${days} days)`} />
            <StatCard
              title="Decided"
              value={inbox.decided.length}
              subtitle={`Approved or dismissed (last ${days} days)`}
              gradientColor="#10b981"
            />
            <StatCard title="Expired" value={inbox.expired.length} subtitle="Not decided in time" gradientColor="#6b7280" />
          </div>

          <Section title="Open" icon={Lightbulb} count={inbox.open.length} testId="growth-open" empty="No open signals.">
            {inbox.open.map((s) => (
              <SignalRow key={s.signal_id} signal={s}>
                {s.risk_tier === 'high' || s.proposal?.risk_tier === 'high' ? (
                  <span className="inline-flex items-center gap-1.5 rounded-md border border-amber-500/30 px-3 py-1.5 text-sm text-amber-300">
                    <ShieldCheck className="h-4 w-4" />
                    {AWAITING_REVIEW_LABEL}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => decide(s, 'approve')}
                    disabled={busyId !== null}
                    className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50"
                  >
                    {busyId === s.signal_id ? <Spinner size="sm" /> : <CheckCircle className="h-4 w-4" />}
                    Approve
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setDismissing(s);
                    setReason('');
                    setDismissError(null);
                  }}
                  disabled={busyId !== null}
                  className="inline-flex items-center gap-1.5 rounded-md border border-gray-600 px-3 py-1.5 text-sm font-medium text-gray-200 hover:bg-gray-700/60 disabled:opacity-50"
                >
                  <XCircle className="h-4 w-4" />
                  Dismiss
                </button>
              </SignalRow>
            ))}
          </Section>

          <Section
            title="Decided"
            icon={History}
            count={inbox.decided.length}
            testId="growth-decided"
            empty={`No decisions in the last ${days} days.`}
          >
            {inbox.decided.map((s) => (
              <SignalRow
                key={s.signal_id}
                signal={s}
                trailing={
                  <div className={`text-xs font-semibold ${s.state === 'dismissed' ? 'text-gray-400' : 'text-emerald-300'}`}>
                    {STATE_LABELS[s.state] ?? humanise(s.state)} {formatDate(s.decided_at)}
                  </div>
                }
              >
                {/* The proposal's own fold: a decision made before it existed never authorises applying it. */}
                {s.state === 'approved' && s.proposal?.state === 'approved' && (
                  <button
                    type="button"
                    onClick={() => decide(s, 'applied')}
                    disabled={busyId !== null}
                    className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-500 disabled:opacity-50"
                  >
                    {busyId === s.signal_id ? <Spinner size="sm" /> : <Wrench className="h-4 w-4" />}
                    Mark applied
                  </button>
                )}
              </SignalRow>
            ))}
          </Section>

          <Section title="Expired" icon={Clock} count={inbox.expired.length} testId="growth-expired" empty="No expired signals.">
            {inbox.expired.map((s) => (
              <SignalRow key={s.signal_id} signal={s} />
            ))}
          </Section>
        </>
      ) : null}

      <Modal title="Dismiss signal" show={dismissing !== null} onClose={() => setDismissing(null)}>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (dismissing && reason.trim() !== '') void decide(dismissing, 'dismiss', reason.trim());
          }}
        >
          <label htmlFor="growth-dismiss-reason" className="block text-sm text-gray-300">
            Why is this signal not worth acting on? A reason is required. Do not include names, email addresses or
            phone numbers.
          </label>
          <textarea
            id="growth-dismiss-reason"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              setDismissError(null);
            }}
            maxLength={500}
            rows={3}
            className="w-full rounded-md border border-gray-700 bg-gray-900/60 px-3 py-2 text-sm text-gray-100"
          />
          {dismissError && (
            <div role="alert" className="rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">
              {dismissError}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setDismissing(null)}
              className="rounded-md border border-gray-600 px-3 py-1.5 text-sm text-gray-200 hover:bg-gray-700/60"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={reason.trim() === '' || busyId !== null}
              className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50"
            >
              {busyId !== null && <Spinner size="sm" />}
              Dismiss signal
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default GrowthInboxPage;
